import { useEffect, useState } from 'react';
import { quizGeneratorAPI } from '../api/quizGeneratorAPI';
import { useAuth } from '../../auth/hooks/useAuth';

const EMPTY = Object.freeze({});

/**
 * The signed-in user's latest attempt per paper or topic id
 * (POST /paper_attempts/statuses), the only source of what a paper row shows.
 *
 * `statusRequestKey` is the ids joined by newlines
 * (topicAttemptStatusRequestKey), so the request reruns only when the ids
 * change. Returns { papers, state }:
 *
 * - `signed_out`: no user, `papers` is {}.
 * - `idle`: no ids to ask about, `papers` is {}.
 * - `pending`: no answer for this user yet. `papers` is {} (never the
 *   previous user's answer).
 * - `ready`: `papers` is the server's answer for this user. When only the
 *   ids change (the page's topics load after its papers), the last answer for
 *   the same user and exam stays `ready` until the new one arrives, so rows
 *   do not flash back to Not started.
 * - `failed`: the request failed; `papers` is {}.
 *
 * The answer is stored with the user id, exam and ids it was asked for, and
 * is returned only while the user and exam still match, so switching account
 * never shows the previous account's statuses, even for one render.
 */
export default function usePaperAttemptStatuses(exam, statusRequestKey) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [answer, setAnswer] = useState(null);

  useEffect(() => {
    if (!userId || !statusRequestKey) return undefined;
    let cancelled = false;
    const ask = { userId, exam, key: statusRequestKey };
    (async () => {
      try {
        const res = await quizGeneratorAPI.getPaperAttemptStatuses(exam, statusRequestKey.split('\n'));
        if (cancelled) return;
        if (res?.success) {
          setAnswer({ ...ask, state: 'ready', papers: res.papers || {} });
        } else {
          setAnswer({ ...ask, state: 'failed', papers: EMPTY });
        }
      } catch (err) {
        if (cancelled) return;
        console.warn(`${exam} paper statuses fetch failed`, err?.status ?? null);
        setAnswer({ ...ask, state: 'failed', papers: EMPTY });
      }
    })();
    return () => { cancelled = true; };
  }, [userId, exam, statusRequestKey]);

  if (!userId) return { papers: EMPTY, state: 'signed_out' };
  if (!statusRequestKey) return { papers: EMPTY, state: 'idle' };
  if (!answer || answer.userId !== userId || answer.exam !== exam) {
    return { papers: EMPTY, state: 'pending' };
  }
  if (answer.key !== statusRequestKey) {
    // The same user's last answer, for some of these ids, until this one lands.
    return answer.state === 'ready'
      ? { papers: answer.papers, state: 'ready' }
      : { papers: EMPTY, state: 'pending' };
  }
  return { papers: answer.papers, state: answer.state };
}
